<template>
      <v-form
        ref="form"
        v-model="isValid"
        @submit.prevent="handleSubmit"
      >
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="formData.to"
              label="Send to"
              :rules="[v => !!v || 'Email is required']"
              variant="outlined"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="formData.subject"
              label="Subject"
              :rules="[v => !!v || 'Subject is required']"
              variant="outlined"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="formData.message"
              label="Message"
              :rules="[v => !!v || 'Message is required']"
              variant="outlined"
              rows="6"
              auto-grow
            />
          </v-col>

          <v-col cols="12" class="d-flex align-center">
            <div v-if="props.orders.length > 0" class="text-body-2 d-flex align-center">
              <span class="mr-2">Attachment: orders.csv</span>
              <v-btn
                variant="text"
                density="comfortable"
                size="small"
                color="primary"
                prepend-icon="mdi-download"
                @click="downloadCSV"
              >
                Download
              </v-btn>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              type="submit"
              :loading="loading"
              :disabled="!isValid"
            >
              Send Message
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'

const props = defineProps({
  to: {
    type: String,
    required: false
  },
  subject: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: false
  },
  orders: {
    type: Array,
    required: false,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['submit', 'back'])

const form = ref(null)
const isValid = ref(false)

const formData = ref({
  to: props.to,
  subject: props.subject,
  message: props.message || ''
})

const formatShortDate = () => {
  const date = new Date()
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const formatZipCode = (zip) => {
  if (!zip) return ''
  // Remove all non-numeric characters and take first 5 digits
  return zip.replace(/\D/g, '').slice(0, 5)
}

const generateCSV = () => {
  // CSV Headers
  const headers = [
    'PONUMBER',
    'BUYER FULL NAME',
    'STREET ADDRESS 1',
    'STREET ADDRESS 2',
    'CITY',
    'STATE',
    'COUNTRY CODE',
    'POSTAL CODE',
    'CONTACT NUMBER',
    'PART NUMBER',
    'QTY',
    'PRICE',
    'SHIPVIA'
  ]

  // Convert orders to CSV rows
  const rows = props.orders.map(order => [
    order.ID,
    order.shippingname,
    order.shippingstreet1,
    order.shippingstreet2 || '',
    order.city,
    order.state,
    '',
    formatZipCode(order.zip),  // Format zip code
    order.phone || '',
    order.itemnumber,
    order.quantity,
    '',
    'F0'
  ])

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n')

  return csvContent
}

const downloadCSV = () => {
  const csvContent = generateCSV()
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `orders-${formatShortDate()}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const handleSubmit = () => {
  if (!form.value.validate()) return

  // Create FormData and append fields
  const formDataToSend = new FormData()
  formDataToSend.append('to', formData.value.to)
  formDataToSend.append('subject', formData.value.subject)
  formDataToSend.append('message', formData.value.message)

  if (props.orders.length > 0) {
    // Create CSV file
    const csvContent = generateCSV()
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const fileName = `orders-${formatShortDate()}.csv`
    
    // Create a File object instead of Blob
    const csvFile = new File([csvBlob], fileName, { type: 'text/csv' })
    formDataToSend.append('attachment', csvFile)
    // Add orders IDs
    formDataToSend.append('orderIds', JSON.stringify(props.orders.map(order => order.ID)))
  }

  // console.log('formDataToSend', formDataToSend.get('to'), formDataToSend.get('subject'), formDataToSend.get('message'), formDataToSend.get('attachment'), formDataToSend.get('orderIds'))
  emit('submit', formDataToSend)
}
</script> 